import { Locale } from '@finxone-platform/shared/enums';
import { AlertTypes } from '@finxone-platform/shared/sys-config-types';
import { CardsErrorCodes } from '../error-codes/cards.type';
import { CardsSuccessCodes } from '../success-codes/cards.type';

const defaultCardsAlerts = {
  // ERROR
  CARD_DOES_NOT_EXIST_TITLE: 'Card Not Found',
  CARD_DOES_NOT_EXIST_DESCRIPTION:
    'This card is not registered in our system. Please verify the card details.',
  FAILED_TO_FETCH_CARD_TITLE: 'Card Error',
  FAILED_TO_FETCH_CARD_DESCRIPTION:
    'There has been an issue retrieving the card details.  Please contact our customer support team.',
  FAILED_TO_ADD_CARD_TITLE: 'Card Error',
  FAILED_TO_ADD_CARD_DESCRIPTION:
    'There has been an issue with adding this card to your account.  Please contact our customer support team.',
  FAILED_TO_UPDATE_CARD_TITLE: 'Card Error',
  FAILED_TO_UPDATE_CARD_DESCRIPTION:
    'There has been an issue updating the card details.  Please contact our customer support team.',
  PROVIDER_USER_NOT_FOUND_TITLE: 'Card Provider User Not Found',
  PROVIDER_USER_NOT_FOUND_DESCRIPTION: 'Your account details cannot be retrieved, please try again later.',
  FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER_TITLE: 'Account Error',
  FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER_DESCRIPTION:
    'There has been an issue retrieving the account details.  Please contact our customer support team.',
  FAILED_TO_FETCH_CARD_TRANSACTIONS_PROVIDER_TITLE: 'Account Error',
  FAILED_TO_FETCH_CARD_TRANSACTIONS_PROVIDER_DESCRIPTION:
    'There has been an issue retrieving the account details.  Please contact our customer support team.',
  CARD_NOT_SPECIFIED_TITLE: 'Card ID Required',
  CARD_NOT_SPECIFIED_DESCRIPTION: 'Please select a card to proceed with this action.',
  CARD_BLOCKED_TITLE: 'Card Blocked',
  CARD_BLOCKED_DESCRIPTION:
    'Your card has been blocked for security reasons. Contact customer support for assistance.',
  // SUCCESS
  CARD_NUMBER_COPIED_TITLE: 'Card Number Copied',
  CARD_NUMBER_COPIED_DESCRIPTION: 'Card number has been copied to your clipboard.',
  CARD_CVV_COPIED_TITLE: 'CVV Copied',
  CARD_CVV_COPIED_DESCRIPTION: 'CVV has been copied to your clipboard.',
  CARD_EXP_COPIED_TITLE: 'Expiry Date Copied',
  CARD_EXP_COPIED_DESCRIPTION: 'Expiry date has been copied to your clipboard.',
  CARD_FROZEN_SUCCESS_TITLE: 'Card Successfully Frozen',
  CARD_FROZEN_SUCCESS_DESCRIPTION:
    'Your card has been temporarily frozen for security. Unfreeze it any time in card settings.',
  CARD_UNFROZEN_SUCCESS_TITLE: 'Card Successfully Unfrozen',
  CARD_UNFROZEN_SUCCESS_DESCRIPTION: 'Your card has been unfrozen and is now ready for use.',
  CARD_RENAMED_SUCCESS_TITLE: 'Card Name Updated',
  CARD_RENAMED_SUCCESS_DESCRIPTION: 'Your card nickname has been successfully updated.',
  CARD_UNBLOCKED_SUCCESS_TITLE: 'Card Access Restored',
  CARD_UNBLOCKED_SUCCESS_DESCRIPTION: 'Your card has been unblocked and is now ready for use.',
  CARD_ACTIVATED_SUCCESS_TITLE: 'Card Activation Successful',
  CARD_ACTIVATED_SUCCESS_DESCRIPTION: 'Your card is now active and ready for use.',
  CARD_PIN_UNLOCKED_ERROR_TITLE: 'Unable to unlock card pin',
  CARD_PIN_UNLOCKED_ERROR_DESCRIPTION: 'Failed to unlock card pin. Please try again.',
  CARD_REISSUE_ERROR_TITLE: 'Unable to reissue card',
  CARD_REISSUE_ERROR_DESCRIPTION: 'Failed to reissue card. Please try again.',
};

export const cardsTranslations: {
  // For new locales just add them here to the extract
  [key in Extract<Locale, Locale.en_GB>]: typeof defaultCardsAlerts;
} = {
  [Locale.en_GB]: defaultCardsAlerts,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateCardsAlerts = (translations: typeof defaultCardsAlerts) => ({
  // ERROR
  [CardsErrorCodes.CARD_DOES_NOT_EXIST]: {
    name: 'Card does not exist',
    type: AlertTypes.ERROR,
    title: translations.CARD_DOES_NOT_EXIST_TITLE,
    description: translations.CARD_DOES_NOT_EXIST_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_FETCH_CARD]: {
    name: 'Failed to fetch card',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_FETCH_CARD_TITLE,
    description: translations.FAILED_TO_FETCH_CARD_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_ADD_CARD]: {
    name: 'Failed to add card',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_ADD_CARD_TITLE,
    description: translations.FAILED_TO_ADD_CARD_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_UPDATE_CARD]: {
    name: 'Failed to update card',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_UPDATE_CARD_TITLE,
    description: translations.FAILED_TO_UPDATE_CARD_DESCRIPTION,
  },
  [CardsErrorCodes.PROVIDER_USER_NOT_FOUND]: {
    name: 'Card provider user not found',
    type: AlertTypes.ERROR,
    title: translations.PROVIDER_USER_NOT_FOUND_TITLE,
    description: translations.PROVIDER_USER_NOT_FOUND_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER]: {
    name: 'Failed to fetch linked accounts from card provider',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER_TITLE,
    description: translations.FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_FETCH_CARD_TRANSACTIONS]: {
    name: 'Failed to fetch card transactions',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_FETCH_CARD_TRANSACTIONS_PROVIDER_TITLE,
    description: translations.FAILED_TO_FETCH_CARD_TRANSACTIONS_PROVIDER_DESCRIPTION,
  },
  [CardsErrorCodes.CARD_NOT_SPECIFIED]: {
    name: 'Card not specified',
    type: AlertTypes.ERROR,
    title: translations.CARD_NOT_SPECIFIED_TITLE,
    description: translations.CARD_NOT_SPECIFIED_DESCRIPTION,
  },
  [CardsErrorCodes.CARD_BLOCKED]: {
    name: 'Card blocked',
    type: AlertTypes.ERROR,
    title: translations.CARD_BLOCKED_TITLE,
    description: translations.CARD_BLOCKED_DESCRIPTION,
  },
  // SUCCESS
  [CardsSuccessCodes.CARD_NUMBER_COPIED]: {
    name: 'Card number copied',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_NUMBER_COPIED_TITLE,
    description: translations.CARD_NUMBER_COPIED_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_CVV_COPIED]: {
    name: 'CVV copied',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_CVV_COPIED_TITLE,
    description: translations.CARD_CVV_COPIED_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_EXP_DATE_COPIED]: {
    name: 'Expiry date copied',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_EXP_COPIED_TITLE,
    description: translations.CARD_EXP_COPIED_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_FROZEN]: {
    name: 'Card frozen',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_FROZEN_SUCCESS_TITLE,
    description: translations.CARD_FROZEN_SUCCESS_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_UNFROZEN]: {
    name: 'Card unfrozen',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_UNFROZEN_SUCCESS_TITLE,
    description: translations.CARD_UNFROZEN_SUCCESS_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_RENAMED]: {
    name: 'Card renamed',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_RENAMED_SUCCESS_TITLE,
    description: translations.CARD_RENAMED_SUCCESS_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_UNBLOCKED]: {
    name: 'Card unblocked',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_UNBLOCKED_SUCCESS_TITLE,
    description: translations.CARD_UNBLOCKED_SUCCESS_DESCRIPTION,
  },
  [CardsSuccessCodes.CARD_ACTIVATED]: {
    name: 'Card activated',
    type: AlertTypes.SUCCESS,
    title: translations.CARD_ACTIVATED_SUCCESS_TITLE,
    description: translations.CARD_ACTIVATED_SUCCESS_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_UNLOCK_CARD_PIN]: {
    name: 'Unable to unlock card pin',
    type: AlertTypes.ERROR,
    title: translations.CARD_PIN_UNLOCKED_ERROR_TITLE,
    description: translations.CARD_PIN_UNLOCKED_ERROR_DESCRIPTION,
  },
  [CardsErrorCodes.FAILED_TO_REISSUE_CARD]: {
    name: 'Unable to reissue card',
    type: AlertTypes.ERROR,
    title: translations.CARD_REISSUE_ERROR_TITLE,
    description: translations.CARD_REISSUE_ERROR_DESCRIPTION,
  },
});
